// Browser-side feedback submission. The GitHub App's private key is server-only,
// so the SPA never talks to GitHub directly — it calls the `feedback` edge
// function with the user's Supabase JWT, and the function holds the credential.
// Nothing under `src/` may import the server-side github helper; this HTTP
// wrapper is the only bridge. Same shape as geoClient.ts.
import { FUNCTIONS_URL, SUPABASE_PUBLISHABLE_KEY, supabase } from './supabaseClient'

export type FeedbackKind = 'bug' | 'feature'

export interface FeedbackInput {
  kind: FeedbackKind
  title: string
  details: string
  /** How the submitter wants to be credited. Falls back to their email server-side. */
  name?: string
}

export interface FiledIssue {
  number: number
  url: string
}

/**
 * Distinguishes "the form was wrong" from "the backend isn't wired up" so the
 * page can say something true in both cases. `code` is the server's error string.
 */
export class FeedbackError extends Error {
  readonly code: string
  constructor(code: string, message: string) {
    super(message)
    this.name = 'FeedbackError'
    this.code = code
  }
}

const EXPIRED_SESSION = 'Your sign-in expired. Sign in again and resend.'

const MESSAGES: Record<string, string> = {
  github_not_configured:
    "Feedback isn't connected to GitHub yet, so nothing was sent. Let an organizer know the app still needs its GitHub App secrets.",
  github_error: 'GitHub turned that away. Nothing was lost — try again in a minute.',
  title_required: 'Give it a one-line summary first.',
  details_required: 'Add a little detail so someone can act on it.',
  invalid_kind: 'Pick whether this is a bug or an idea.',
  unauthorized: EXPIRED_SESSION,
}

/**
 * File a bug or feature request as a GitHub issue. Resolves with the issue number
 * and URL. Throws FeedbackError with a human-readable message on any failure.
 */
export async function submitFeedback(input: FeedbackInput): Promise<FiledIssue> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session) throw new FeedbackError('unauthorized', EXPIRED_SESSION)

  let res: Response
  try {
    res = await fetch(`${FUNCTIONS_URL}/feedback`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        apikey: SUPABASE_PUBLISHABLE_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        kind: input.kind,
        title: input.title,
        details: input.details,
        name: input.name ?? null,
        // Where they were when it went wrong — the single most useful line in a
        // bug report, and the one nobody thinks to include.
        page: window.location.pathname + window.location.search,
      }),
    })
  } catch {
    throw new FeedbackError('network_error', 'Could not reach the server. Check your connection.')
  }

  if (!res.ok) {
    const code = await res
      .json()
      .then((body: unknown) => (body as { error?: string } | null)?.error)
      .catch(() => undefined)
    const key = code ?? `http_${res.status}`
    throw new FeedbackError(key, MESSAGES[key] ?? `Submitting failed (${res.status}).`)
  }

  return (await res.json()) as FiledIssue
}
